import { Button } from '@npo/player-ui-core';
import type { NpoVideoPlayer } from '@npo/player';

const EMBED_BUTTON_CLASS = 'npoplayer-embed-button';

/**
 * "Embed" button that copies an embed code to the clipboard. Extends the
 * player's own Button base (from @npo/player-ui-core) so it gets the same
 * focus/tabindex handling, click-event bus wiring, and icon styling
 * convention as the native buttons (PiP, fullscreen, cast, ...).
 */
class EmbedButton extends Button {
  constructor(
    player: ConstructorParameters<typeof Button>[0],
    appTitle: string,
    videoId: string,
    eventDispatcher: (key: string, value: object | undefined) => void,
  ) {
    super(player, { className: EMBED_BUTTON_CLASS });

    this.setAriaLabel('Embed');
    this.onClick = async () => {
      await navigator.clipboard.writeText(
        `<iframe width="560" height="315" src="https://player.avrotros.nl/${appTitle.toLowerCase()}/${videoId}" title="AVROTROS" allow="autoplay; encrypted-media; picture-in-picture;" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>`,
      );
      eventDispatcher('snackbar:show', { message: 'Code gekopieërd' });
    };
  }
}

/**
 * Adds the custom "Embed" button to the NPO Player's bottom-bar controls.
 *
 * @param player - The instance of the NPO Player.
 * @param videoId - ID of video.
 */
export default function useNpoPlayerEmbed(player: NpoVideoPlayer, videoId: string): void {
  const { $event } = useNuxtApp();
  const runtimeConfig = useRuntimeConfig();

  // Relies on @npo/player's internal DOM structure — not a public API, may break on upgrade.
  const container = player.container.querySelector<HTMLElement>(
    '.npoplayer-bottom-bar-container-right',
  );

  if (!container) {
    console.warn('useNpoPlayerEmbed: bottom-bar controls not found on the player instance.');
    return;
  }

  // Avoid adding a second button if this composable runs again for the same player instance.
  if (container.querySelector(`.${EMBED_BUTTON_CLASS}`)) return;

  const embedButton = new EmbedButton(
    player.player,
    runtimeConfig.public.embedEnvironment,
    videoId,
    $event as never,
  );
  embedButton.render(container);

  // Keep the Embed button directly before PiP to match the intended control order.
  const pipButton = container.querySelector('.npoplayer-pip-button');
  if (pipButton) container.insertBefore(embedButton.element, pipButton);
}
